import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LifeBuoy, MessageSquareDashed, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import {
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/shadcn/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@stockline/ui/components/dropdown-menu";
import { useFeatureFlags } from "@/query/hooks/useFeatureFlags";
import { InstantFeedbackDialog } from "./InstantFeedbackDialog";

export interface SidebarFeedbackButtonProps {
  isActive?: boolean;
}

export function SidebarFeedbackButton({
  isActive = false,
}: SidebarFeedbackButtonProps) {
  const { t } = useTranslation();
  const { data: flags } = useFeatureFlags();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Default-on: hide only when the company explicitly flipped the flag off.
  // Loading state (`flags === undefined`) keeps the button visible so a slow
  // flag fetch doesn't blank it for the whole session.
  if (flags?.sidebarShowFeedback === false) return null;

  return (
    <>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              isActive={isActive}
              tooltip={t("navigation.feedback", "Feedback")}
              className="group-data-[collapsible=icon]:justify-center"
              data-testid="sidebar-link-feedback"
            >
              <LifeBuoy size={20} />
              <span className="group-data-[collapsible=icon]:hidden">
                {t("navigation.feedback", "Feedback")}
              </span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-60">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("feedback.menu_label", "Share something with us")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setDialogOpen(true);
              }}
              className="gap-2"
              data-testid="feedback-menu-instant"
            >
              <MessageSquareDashed size={14} className="text-[#00AB55]" />
              <div className="flex flex-col">
                <span className="text-sm">
                  {t("feedback.menu_instant", "Quick feedback")}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {t(
                    "feedback.menu_instant_hint",
                    "Stay on this page, attach a screenshot",
                  )}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="gap-2">
              <Link to="/feedback" data-testid="feedback-menu-page">
                <Inbox size={14} />
                <div className="flex flex-col">
                  <span className="text-sm">
                    {t("feedback.menu_open", "Open feedback inbox")}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {t(
                      "feedback.menu_open_hint",
                      "Browse all threads and replies",
                    )}
                  </span>
                </div>
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      {/* Lazy mount: the dialog body calls `useFeedbackMutations` →
          `useAISync` on every render, which throws when rendered outside
          AISyncProvider (sidebar unit tests do exactly this). Only mounting
          on demand keeps the sidebar renderable in isolation. */}
      {dialogOpen && (
        <InstantFeedbackDialog open onOpenChange={setDialogOpen} />
      )}
    </>
  );
}
